import * as DialogPrimitive from '@radix-ui/react-dialog'
import { X } from 'lucide-react'
import { cn } from '@/lib/utils'

export const Sheet = DialogPrimitive.Root
export const SheetTrigger = DialogPrimitive.Trigger
export const SheetClose = DialogPrimitive.Close

export function SheetContent({
  className,
  children,
  side = 'bottom',
  showCloseButton = true,
  showDragHandle = true,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Content> & {
  side?: 'top' | 'bottom' | 'right' | 'full' | 'center'
  showCloseButton?: boolean
  showDragHandle?: boolean
}) {
  return (
    <DialogPrimitive.Portal>
      <DialogPrimitive.Overlay
        className={cn(
          'fixed inset-0 z-60 bg-neutral-900/40 backdrop-blur-sm',
          side === 'full' && 'bg-white/0 backdrop-blur-none',
        )}
      />
      <DialogPrimitive.Content
        className={cn(
          'fixed z-60 flex flex-col bg-white shadow-xl focus:outline-none',
          side === 'top' &&
            'inset-x-0 top-0 max-h-[92vh] rounded-b-2xl safe-top',
          side === 'bottom' &&
            'inset-x-0 bottom-0 max-h-[92vh] rounded-t-2xl pb-safe',
          side === 'right' && 'inset-y-0 right-0 h-full w-full max-w-md',
          side === 'full' && 'inset-0 z-60 h-dvh max-h-dvh w-full rounded-none',
          side === 'center' &&
            'left-1/2 top-1/2 w-[calc(100vw-2rem)] max-w-md -translate-x-1/2 -translate-y-1/2 rounded-2xl',
          className,
        )}
        {...props}
      >
        {side === 'bottom' && showDragHandle && (
          <div className="mx-auto mt-3 h-1.5 w-12 shrink-0 rounded-full bg-neutral-200" />
        )}
        {children}
        {showCloseButton && (
          <DialogPrimitive.Close
            className="absolute right-4 top-4 rounded-full p-1.5 text-neutral-500 hover:bg-neutral-100"
            aria-label="Close"
          >
            <X className="h-5 w-5" aria-hidden="true" />
          </DialogPrimitive.Close>
        )}
      </DialogPrimitive.Content>
    </DialogPrimitive.Portal>
  )
}

export function SheetHeader({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) {
  return <div className={cn('border-b border-neutral-100 px-5 py-4', className)} {...props} />
}

export function SheetTitle({ className, ...props }: React.ComponentProps<typeof DialogPrimitive.Title>) {
  return (
    <DialogPrimitive.Title
      className={cn('font-display text-lg font-bold text-neutral-900', className)}
      {...props}
    />
  )
}

export function SheetBody({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) {
  return <div className={cn('flex-1 overflow-y-auto px-5 py-4', className)} {...props} />
}

export function SheetFooter({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={cn('border-t border-neutral-100 bg-white p-4 safe-bottom', className)}
      {...props}
    />
  )
}
